import { useEffect, useState, useMemo } from "react";
import Joyride, { CallBackProps, Step, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { useTour } from "@/contexts/TourContext";
import { TourErrorBoundary } from "./TourErrorBoundary";
import { TourLogger } from "@/utils/tourLogger";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
// Utility function to wait for an element to exist
const waitForElement = (selector: string, maxAttempts = 5, delayMs = 500): Promise<boolean> => {
  return new Promise((resolve) => {
    let attempts = 0;
    const check = () => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(true);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, delayMs);
      } else {
        resolve(false);
      }
    };
    check();
  });
};


export const InteractiveTour = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isTourRunning, stopTour } = useTour();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourLocked, setTourLocked] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Desktop tour steps
  const desktopSteps: Step[] = useMemo(() => [
    {
      target: "body",
      content: "Welcome to Sendfeedback.ai! Let's get you set up in under 2 minutes. We'll guide you through creating your first feedback page.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="dashboard-overview"]',
      content: "This is your command center. Once you start collecting feedback, key metrics and insights will appear here.",
      placement: "bottom",
    },
    {
      target: '[data-tour="create-page"]',
      content: "Click here to create your first feedback page. It only takes 2 minutes!",
      placement: "bottom",
    },
    {
      target: '[data-tour="sidebar-pages"]',
      content: "Your feedback pages will appear here. You can create multiple pages for different teams or products.",
      placement: "right",
    },
    {
      target: '[data-tour="sidebar-navigation"]',
      content: "Access Analytics, Email Campaigns, Team Management, and more from your sidebar.",
      placement: "right",
    },
    {
      target: '[data-tour="sidebar-ai-insights"]',
      content: "Access AI Insights for automated analysis of your feedback. Get sentiment trends, priority recommendations, and skills gap analysis powered by AI.",
      placement: "right",
    },
    {
      target: '[data-tour="sidebar-ai-assistant"]',
      content: "Chat with your AI Assistant anytime! Ask questions about your feedback, get recommendations, or explore insights conversationally.",
      placement: "right",
    },
    {
      target: "body",
      content: "You're all set! Start collecting feedback and watch the insights roll in. You can restart this tour anytime from the sidebar.",
      placement: "center",
    },
  ], []);

  // Mobile tour steps - targets bottom navigation instead of sidebar
  const mobileSteps: Step[] = useMemo(() => [
    {
      target: "body",
      content: "Welcome to Sendfeedback.ai! Let's get you set up in under 2 minutes.",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: '[data-tour="dashboard-overview"]',
      content: "This is your command center. Key metrics and insights will appear here.",
      placement: "bottom",
    },
    {
      target: '[data-tour="create-page"]',
      content: "Tap here to create your first feedback page!",
      placement: "bottom",
    },
    {
      target: '[data-tour="mobile-pages-nav"]',
      content: "Tap here to access your feedback pages anytime.",
      placement: "top",
    },
    {
      target: '[data-tour="mobile-navigation"]',
      content: "Use the bottom navigation to access Analytics, Feedback, and Settings.",
      placement: "top",
    },
    {
      target: '[data-tour="mobile-ai-insights"]',
      content: "Tap AI Insights for automated feedback analysis, sentiment trends, and recommendations.",
      placement: "top",
    },
    {
      target: '[data-tour="mobile-ai-assistant"]',
      content: "Chat with your AI Assistant for instant answers about your feedback data.",
      placement: "top",
    },
    {
      target: "body",
      content: "You're all set! Start collecting feedback. Restart this tour anytime from Settings.",
      placement: "center",
    },
  ], []);

  // Select appropriate steps based on device
  const tourSteps = isMobile ? mobileSteps : desktopSteps;

  // Joyride styling
  const joyrideStyles = {
    options: {
      primaryColor: "hsl(var(--primary))",
      zIndex: 10000,
    },
    tooltipContent: {
      padding: window.innerWidth < 768 ? "12px" : "20px",
    },
    buttonNext: {
      backgroundColor: "hsl(var(--primary))",
      fontSize: window.innerWidth < 768 ? "14px" : "16px",
      padding: window.innerWidth < 768 ? "8px 16px" : "10px 20px",
      borderRadius: "8px",
    },
    buttonBack: {
      marginRight: 10,
      fontSize: window.innerWidth < 768 ? "14px" : "16px",
    },
    buttonSkip: {
      fontSize: window.innerWidth < 768 ? "14px" : "16px",
    },
  };

  // Tour is now started via TourPrompt component - removed auto-start logic

  // Restore tour state on page refresh
  useEffect(() => {
    const savedRun = sessionStorage.getItem("tourRunning");
    const savedStep = sessionStorage.getItem("tourStep");

    if (savedRun === "true" && savedStep) {
      setRun(true);
      setStepIndex(parseInt(savedStep, 10));
    }
  }, []);

  // Save tour state
  useEffect(() => {
    if (run) {
      sessionStorage.setItem("tourRunning", "true");
      sessionStorage.setItem("tourStep", stepIndex.toString());
    } else {
      sessionStorage.removeItem("tourRunning");
      sessionStorage.removeItem("tourStep");
    }
  }, [run, stepIndex]);

  // Manual tour start from sidebar
  useEffect(() => {
    const startManualTour = async () => {
      if (isTourRunning && !run) {
        navigate("/dashboard");
        
        // Wait for dashboard to load
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setRun(true);
        setStepIndex(0);
        TourLogger.tourStarted("unified", tourSteps.length);
      }
    };
    
    startManualTour();
  }, [isTourRunning, run, navigate, tourSteps.length]);

  // Handle tour events
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, index, action } = data;

    if (tourLocked) return;

    // Note: Sidebar groups are pre-expanded before tour starts to prevent race condition

    if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      
      // Log step completion
      if (action === ACTIONS.NEXT) {
        TourLogger.stepCompleted("unified", index + 1, tourSteps.length);
      }

      setStepIndex(nextStepIndex);
    }

    // Handle missing elements - continue to next step instead of ending tour
    if (type === EVENTS.TARGET_NOT_FOUND) {
      const currentStep = tourSteps[index];
      const target = typeof currentStep.target === 'string' ? currentStep.target : '';
      
      TourLogger.missingElement(target, "unified");
      
      // Continue to next step instead of ending tour
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      if (nextStepIndex < tourSteps.length && nextStepIndex >= 0) {
        setStepIndex(nextStepIndex);
      } else {
        // If we've reached the end, finish the tour
        setRun(false);
        setStepIndex(0);
        stopTour();
        localStorage.setItem("tourCompleted", "true");
        TourLogger.tourCompleted("unified");
      }
    }

    if (status === STATUS.FINISHED) {
      setRun(false);
      setStepIndex(0);
      stopTour(); // Reset tour context
      localStorage.setItem("tourCompleted", "true");
      sessionStorage.removeItem("tourRunning");
      sessionStorage.removeItem("tourStep");
      TourLogger.tourCompleted("unified");

      toast({
        title: "Tour Complete! 🎉",
        description: "You're ready to start collecting feedback. Create your first page to get started!",
      });
    }

    if (status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      stopTour(); // Reset tour context
      localStorage.setItem("tourCompleted", "true");
      sessionStorage.removeItem("tourRunning");
      sessionStorage.removeItem("tourStep");
      TourLogger.tourSkipped("unified", index, tourSteps.length);

      toast({
        title: "Tour Skipped",
        description: "You can restart the tour anytime from the sidebar.",
      });
    }
  };

  if (!user) return null;
  if (!run) return null;

  return (
    <TourErrorBoundary>
      <Joyride
        steps={tourSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose
        disableCloseOnEsc
        styles={joyrideStyles}
        callback={handleJoyrideCallback}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
          skip: "Skip Tour",
        }}
      />
    </TourErrorBoundary>
  );
};
